import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { selectSettingsActive, selectLeaveGamePopupActive } from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";
import { usePrimaryUIToolbarDimensionsV2 } from "../../../../Hooks/usePrimaryUIToolbarDimensionsV2";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import {
  toggleBackgroundMusic,
  toggleSoundEffects,
  toggleGlobalSound,
} from "../../../../State/actions";
import { soundManager } from "../../../../SoundManager";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import { SOUND_NAME } from "../../../../sounds";

// Theme colors (matching backport UI)
const ACCENT_COLOR = "#ffbf11";
const TRACK_COLOR = "rgba(13, 22, 84, 0.8)";

// Animation timing (matching WarningPopup / GameRulesSection)
const OVERLAY_FADE_DURATION = 200;
const CONTENT_ANIM_DURATION = 250;

// Toggle switch component
const ToggleSwitch = ({ checked, onChange, size = 52 }) => {
  const knobPadding = 3;
  const height = Math.round(size * 0.55);
  const knobSize = height - knobPadding * 2;
  const knobOffset = checked ? size - knobSize - knobPadding : knobPadding;

  const handleClick = useCallback(() => {
    onChange(!checked);
  }, [checked, onChange]);

  return (
    <div
      onClick={handleClick}
      style={{
        width: `${size}px`,
        height: `${height}px`,
        borderRadius: `${height / 2}px`,
        backgroundColor: checked ? ACCENT_COLOR : "rgba(120, 120, 140, 0.5)",
        cursor: "pointer",
        position: "relative",
        transition: "background-color 200ms ease",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: `${knobSize}px`,
          height: `${knobSize}px`,
          borderRadius: "50%",
          backgroundColor: "#ffffff",
          position: "absolute",
          top: `${knobPadding}px`,
          left: `${knobOffset}px`,
          transition: "left 200ms ease",
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        }}
      />
    </div>
  );
};

// Language selector dropdown
const LanguageSelector = ({ currentLang, onChange, size = 52 }) => {
  const height = Math.round(size * 0.54);

  const handleChange = useCallback(
    (e) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  // Get available languages from i18n
  const languages = [
    "en",
    "de",
    "es",
    "fr",
    "it",
    "pt",
    "ru",
    "tr",
    "ja",
    "ko",
    "zh",
  ];

  return (
    <select
      value={currentLang}
      onChange={handleChange}
      style={{
        width: `${size}px`,
        height: `${height}px`,
        borderRadius: `${height / 2}px`,
        backgroundColor: ACCENT_COLOR,
        color: "#090f41",
        border: "none",
        cursor: "pointer",
        fontSize: `${Math.round(height * 0.5)}px`,
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: "800",
        textAlign: "center",
        textAlignLast: "center",
        outline: "none",
        appearance: "none",
        WebkitAppearance: "none",
        paddingLeft: `${Math.round(size * 0.15)}px`,
        paddingRight: `${Math.round(size * 0.15)}px`,
        textTransform: "uppercase",
      }}
    >
      {languages.map((lang) => (
        <option
          key={lang}
          value={lang}
          style={{ backgroundColor: "#090f41", color: "#ffffff" }}
        >
          {lang.toUpperCase()}
        </option>
      ))}
    </select>
  );
};

const SettingsSection = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const settingsActive = useSelector(selectSettingsActive);
  const leaveGamePopupActive = useSelector(selectLeaveGamePopupActive);
  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    getResponsiveConfig,
  } = useDimensions();
  const toolbar = usePrimaryUIToolbarDimensionsV2();

  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Sound state from Redux
  const { soundEffectsEnabled, backgroundMusicEnabled, globalSoundDisabled } =
    useSelector((state) => state.sound);

  // Animation state
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const prevActiveRef = useRef(false);

  // Top toolbar height
  const topToolbarHeight = useMemo(() => {
    return buttonConfig.height * 1.0;
  }, [buttonConfig.height]);

  // Bottom toolbar Y
  const bottomToolbarY = useMemo(() => {
    return dimensions.height - toolbar.height;
  }, [dimensions.height, toolbar.height]);

  // Scroll area
  const scrollArea = useMemo(
    () => ({
      top: topToolbarHeight,
      height: bottomToolbarY - topToolbarHeight,
      width: dimensions.width,
    }),
    [topToolbarHeight, bottomToolbarY, dimensions.width],
  );

  // Responsive font sizes (matching GameRulesSection)
  const fontSizes = useMemo(() => {
    let multiplier = 1.0;
    if (device === PixiSettings.DEVICE.MOBILE) {
      multiplier =
        orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.8 : 1.5;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multiplier =
        orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.5 : 1.5;
    }
    const base = 28 * multiplier * uiSymbolScale;
    return {
      pageTitle: Math.floor(base * 1.5 * 1.1),
      label: Math.floor(base * 1.1),
      description: Math.floor(base * 0.75),
      toggleSize: Math.floor(base * 2.8),
    };
  }, [device, orientation, uiSymbolScale]);

  // Content max width (matching GameRulesSection)
  const contentMaxWidth = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? dimensions.width * 0.75
        : toolbar.width * 0.55;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? dimensions.width * 0.55
        : toolbar.width * 0.45;
    }
    return toolbar.width * 0.35;
  }, [device, orientation, dimensions.width, toolbar.width]);

  // Toggle handlers
  const handleAllSoundsToggle = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    // "All Sounds" acts as master toggle — uses toggleGlobalSound
    // which saves/restores previous states for music and effects
    dispatch(toggleGlobalSound());
  }, [dispatch]);

  const handleSoundEffectsToggle = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    dispatch(toggleSoundEffects());
  }, [dispatch]);

  const handleMusicToggle = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    dispatch(toggleBackgroundMusic());
  }, [dispatch]);

  const handleLanguageChange = useCallback(
    (lang) => {
      soundManager.play(SOUND_NAME.button_click);
      i18n.changeLanguage(lang);
    },
    [i18n],
  );

  // Derived state — "All Sounds" is ON when global sound is not disabled
  const allSoundsEnabled = !globalSoundDisabled;

  // Fade-in / fade-out logic (matching GameRulesSection)
  useEffect(() => {
    let fadeInTimer;
    let fadeOutTimer;

    if (settingsActive && !prevActiveRef.current) {
      setShouldRender(true);
      setIsVisible(false);
      fadeInTimer = setTimeout(() => {
        setIsVisible(true);
      }, OVERLAY_FADE_DURATION);
    } else if (!settingsActive && prevActiveRef.current) {
      setIsVisible(false);
      fadeOutTimer = setTimeout(() => {
        setShouldRender(false);
      }, CONTENT_ANIM_DURATION);
    }

    prevActiveRef.current = settingsActive;

    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(fadeOutTimer);
    };
  }, [settingsActive]);

  if (!shouldRender) return null;

  const settingsItems = [
    {
      label: t("settings_audio_label", { ns: "common" }),
      description: t("settings_audio_description", { ns: "common" }),
      type: "toggle",
      checked: allSoundsEnabled,
      onChange: handleAllSoundsToggle,
    },
    {
      label: t("settings_sound_effects_label", { ns: "common" }),
      description: t("settings_sound_effects_description", { ns: "common" }),
      type: "toggle",
      checked: soundEffectsEnabled,
      onChange: handleSoundEffectsToggle,
    },
    {
      label: t("settings_music_label", { ns: "common" }),
      description: t("settings_music_description", { ns: "common" }),
      type: "toggle",
      checked: backgroundMusicEnabled,
      onChange: handleMusicToggle,
    },
    ...(FEATURES_CONFIG.LANGUAGE_SWITCH.ENABLED
      ? [
          {
            label: "Language",
            description: "Select your preferred language",
            type: "language",
            currentLang: i18n.language,
            onChange: handleLanguageChange,
          },
        ]
      : []),
  ];

  return (
    <>
      {/* Scroll area — same pattern as GameRulesSection */}
      <div
        className="backport-settings-scroll"
        style={{
          position: "fixed",
          top: `${scrollArea.top}px`,
          left: 0,
          width: `${dimensions.width}px`,
          height: `${scrollArea.height}px`,
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: `${fontSizes.label}px`,
          paddingBottom: `${fontSizes.label}px`,
          paddingLeft: `${(dimensions.width - contentMaxWidth) / 2}px`,
          paddingRight: `${(dimensions.width - contentMaxWidth) / 2}px`,
          boxSizing: "border-box",
          zIndex: 9500,
          pointerEvents: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${ACCENT_COLOR} ${TRACK_COLOR}`,
          // Fade animation
          display: leaveGamePopupActive ? "none" : "block",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.95)",
          transition: `opacity ${CONTENT_ANIM_DURATION}ms ease-out, transform ${CONTENT_ANIM_DURATION}ms ease-out`,
        }}
      >
        {/* Settings Title */}
        <h2 className="backport-settings-title">{t("settings_title", { ns: "common" }).toUpperCase()}</h2>

        {/* Settings Items */}
        {settingsItems.map((item, index) => (
          <div key={index} className="backport-settings-row">
            <div className="backport-settings-row-text">
              <div className="backport-settings-label">{item.label}</div>
              <div className="backport-settings-description">
                {item.description}
              </div>
            </div>
            <div className="backport-settings-row-control">
              {item.type === "toggle" ? (
                <ToggleSwitch
                  checked={item.checked}
                  onChange={item.onChange}
                  size={fontSizes.toggleSize}
                />
              ) : (
                <LanguageSelector
                  currentLang={item.currentLang}
                  onChange={item.onChange}
                  size={fontSizes.toggleSize}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Scoped styles */}
      <style>{`
        .backport-settings-title {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.pageTitle}px;
          font-weight: 800;
          color: ${ACCENT_COLOR};
          text-align: center;
          margin: ${Math.round(fontSizes.label * 0.5)}px 0 ${Math.round(fontSizes.label * 1.8)}px 0;
          line-height: ${Math.floor(fontSizes.pageTitle * 1.3)}px;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
        }

        .backport-settings-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: ${Math.round(fontSizes.label * 0.8)}px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        }

        .backport-settings-row:last-child {
          border-bottom: none;
        }

        .backport-settings-row-text {
          flex: 1;
          min-width: 0;
        }

        .backport-settings-row-control {
          flex-shrink: 0;
          margin-left: ${fontSizes.label}px;
        }

        .backport-settings-label {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.label}px;
          font-weight: 700;
          color: #ffffff;
          text-transform: uppercase;
          text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
          line-height: ${Math.floor(fontSizes.label * 1.3)}px;
        }

        .backport-settings-description {
          font-family: 'Noto Sans', sans-serif;
          font-size: ${fontSizes.description}px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.55);
          text-transform: uppercase;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
          line-height: ${Math.floor(fontSizes.description * 1.4)}px;
          margin-top: ${Math.round(fontSizes.description * 0.2)}px;
        }

        /* Webkit scrollbar */
        .backport-settings-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .backport-settings-scroll::-webkit-scrollbar-track {
          background: ${TRACK_COLOR};
        }
        .backport-settings-scroll::-webkit-scrollbar-thumb {
          background: ${ACCENT_COLOR};
        }
        .backport-settings-scroll::-webkit-scrollbar-thumb:hover {
          background: #ffd044;
        }
      `}</style>
    </>
  );
};

export default SettingsSection;
